import { z } from "zod";

// Formik's prepareDataForValidation turns "" into undefined before the schema
// runs, so a bare z.string() fails as invalid_type and emits zod's own English
// "Required" instead of the key on .min(). The type-level messages fix that.
const requiredString = (message = "validation.required") =>
  z
    .string({ required_error: message, invalid_type_error: message })
    .trim()
    .min(1, message);

// Same prepareDataForValidation trap for optional fields: an empty input
// reaches zod as undefined, so a bare z.string() wrongly fails as "Required".
// Every optional string field in a form schema must go through this helper.
const optionalString = () => z.string().optional();

// z.literal(true) would infer the value type as `true`, which no initial value
// can satisfy — keep it a boolean and reject false in a refine.
const requiredChecked = (message = "validation.required") =>
  z
    .boolean({ required_error: message, invalid_type_error: message })
    .refine((checked) => checked, message);

// An unpicked date field holds null, so the schema keeps null in z.infer (the
// form type needs the display state) and rejects it in a refine carrying the
// key. required_error covers the undefined an absent value produces.
const requiredDate = (message = "validation.required") =>
  z
    .date({ required_error: message, invalid_type_error: message })
    .nullable()
    // The explicit boolean keeps TS from inferring a type predicate, which
    // would narrow z.infer to Date and lose the null display state.
    .refine((date): boolean => date !== null, message);

// NumberField keeps "" while the input is empty and prepareDataForValidation
// rewrites that "" to undefined before the schema runs — required has to
// reject both, and z.infer has to keep the "" display state so initial values
// type-check without hand-written overrides. Chain further rules with an ""
// guard: requiredNumber().refine((v) => v === "" || v > 0, key).
const requiredNumber = (message = "validation.required") =>
  z
    .union([z.number({ invalid_type_error: message }), z.literal("")])
    .optional()
    .transform((value) => value ?? "")
    // Explicit boolean for the same reason as requiredDate — an inferred
    // predicate would narrow z.infer to number and lose the "" display state.
    .refine((value): boolean => value !== "", message);

// Optional counterpart: empty is valid. Accepts the undefined the rewrite
// produces and normalizes it back to "" so z.infer stays number | "".
const optionalNumber = () =>
  z
    .union([z.number(), z.literal("")])
    .optional()
    .transform((value) => value ?? "");

// An unpicked file field holds null and the inferred type has to keep it —
// .refine() after .nullable() would narrow null away, hence superRefine. The
// instanceof message covers a non-File garbage value with the same key.
const requiredFile = (message = "validation.required") =>
  z
    .instanceof(File, { message })
    .nullable()
    .superRefine((file, ctx) => {
      if (!file) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message });
      }
    });

export {
  requiredString,
  optionalString,
  requiredChecked,
  requiredDate,
  requiredNumber,
  optionalNumber,
  requiredFile,
};
